import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Tabs, SpinLoading, AutoCenter, ErrorBlock } from 'antd-mobile';
import BarcodeButton from 'utility/BarcodeButton';
import { WebviewHttpHandler } from 'shared/webviewHttpHandler';
import PaymentDetails from 'utility/PaymentDetails';
import secureStorage from 'shared/secureStorage';

const convertCustomDate = (originalDateString) => {
  // 輸入範例："2014/09/01 16:06"
  // 輸出範例：{"2014/09/01", "週一 04:06 PM"}
  const originalDate = new Date(originalDateString.replace(/-/g, '/'));

  const formattedDate1 = originalDate
    .toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\//g, '/');

  const days = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];
  const dayOfWeek = days[originalDate.getDay()];
  const formattedTime = originalDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const formattedDate2 = `${dayOfWeek} ${formattedTime}`;

  return { formattedDate1, formattedDate2 };
};

const UnpaidList = ({
  datetimeStr,
  price,
  barcodeList,
  polno,
  photoaddr,
  isLastItem,
}) => {
  const { formattedDate1, formattedDate2 } = convertCustomDate(datetimeStr);
  return (
    <div>
      <div className="my-6">
        <div className="space-between">
          <div className="text-body font-normal mb-4">
            <div className="mb-1">{formattedDate1}</div>
            {/* <div>{formattedDate2}</div> */}
          </div>
          <div className="subheading-s font-bold red">$ {price}</div>
        </div>
        <div className="space-between">
          <PaymentDetails polno={polno} photoaddr={photoaddr} />
          <BarcodeButton codeList={barcodeList} />
        </div>
      </div>
      {!isLastItem && <hr />}
    </div>
  );
};

const PaidList = ({ datetimeStr, price, polno, photoaddr, isLastItem }) => {
  const { formattedDate1, formattedDate2 } = convertCustomDate(datetimeStr);
  return (
    <div>
      <div className="my-6">
        <div className="space-between">
          <div className="text-body font-normal mb-4">
            <div className="mb-1">{formattedDate1}</div>
            {/* <div>{formattedDate2}</div> */}
          </div>
          <div className="subheading-s font-bold red">$ {price}</div>
        </div>
        <PaymentDetails polno={polno} photoaddr={photoaddr} />
      </div>
      {!isLastItem && <hr />}
    </div>
  );
};

const SelfPaidList = ({ datetimeStr, price, polno, photoaddr, isLastItem }) => {
  const { formattedDate1, formattedDate2 } = convertCustomDate(datetimeStr);
  return (
    <div>
      <div className="my-6">
        <div className="text-sm font-normal red mb-1">
          請勿依此罰單繳費，避免計次，監理單位另寄發罰單再行辦理
        </div>
        <div className="space-between">
          <div className="text-body font-normal mb-4">
            <div className="mb-1">繳費期限：{formattedDate1}</div>
            {/* <div>{formattedDate2}</div> */}
          </div>
          <div className="subheading-s font-bold red">$ {price}</div>
        </div>
        <PaymentDetails polno={polno} photoaddr={photoaddr} />
      </div>
      {!isLastItem && <hr />}
    </div>
  );
};

const FineTicket = () => {
  const httpHandler = new WebviewHttpHandler(true);
  const location = useLocation();
  const {
    plateNumber = '',
    color = '',
    brand = '',
    model = '',
    year = '',
    displacement = '',
  } = location.state || {};

  const [unpaidList, setUnpaidList] = useState([]);
  const [paidList, setPaidList] = useState([]);
  const [selfPaidList, setSelfPaidList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const getFineData = () => {
    setIsLoading(true);
    const lineUserId = secureStorage.getItem('lineUserId');
    httpHandler
      .get(
        `/service/member/fee?line=${lineUserId}&car=${plateNumber}&category=0`
      )
      .then((response) => {
        if (response.success) {
          updateFineData(response.data);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const updateFineData = (fineList) => {
    let unpaid = [];
    let paid = [];
    let selfPaid = [];

    for (const fine of fineList) {
      switch (fine.status) {
        case 1:
          unpaid.push(fine);
          break;

        case 2:
          paid.push(fine);
          break;

        case 3:
          selfPaid.push(fine);
          break;

        default:
          break;
      }
    }

    setUnpaidList(unpaid);
    setPaidList(paid);
    setSelfPaidList(selfPaid);
  };

  useEffect(() => {
    getFineData();
  }, []);

  return (
    <>
      <div className="bill-container">
        <div className="heading font-bold mb-6 ml-3">罰單</div>
        <Tabs>
          <Tabs.Tab title="代繳未繳" key="unpaid">
            {isLoading ? (
              <AutoCenter className="mt-8">
                <SpinLoading />
              </AutoCenter>
            ) : unpaidList.length === 0 ? (
              <ErrorBlock
                status="empty"
                title={<>沒有罰單紀錄</>}
                description={<></>}
              />
            ) : (
              <div>
                {unpaidList.map((detail, index) => {
                  return (
                    <UnpaidList
                      key={detail.polno}
                      datetimeStr={detail.ticket}
                      price={detail.amt}
                      barcodeList={detail.code}
                      polno={detail.polno}
                      photoaddr={detail.photoaddr}
                      isLastItem={index === paidList.length - 1}
                    />
                  );
                })}
              </div>
            )}
          </Tabs.Tab>
          <Tabs.Tab title="代繳已繳" key="paid">
            {isLoading ? (
              <AutoCenter className="mt-8">
                <SpinLoading />
              </AutoCenter>
            ) : paidList.length === 0 ? (
              <ErrorBlock
                status="empty"
                title={<>沒有罰單紀錄</>}
                description={<></>}
              />
            ) : (
              <div>
                {paidList.map((detail, index) => (
                  <PaidList
                    key={detail.polno}
                    datetimeStr={detail.ticket}
                    price={detail.amt}
                    polno={detail.polno}
                    photoaddr={detail.photoaddr}
                    isLastItem={index === paidList.length - 1}
                  />
                ))}
              </div>
            )}
          </Tabs.Tab>
          <Tabs.Tab title="已轉罰" key="self-paid">
            {isLoading ? (
              <AutoCenter className="mt-8">
                <SpinLoading />
              </AutoCenter>
            ) : selfPaidList.length === 0 ? (
              <ErrorBlock
                status="empty"
                title={<>沒有罰單紀錄</>}
                description={<></>}
              />
            ) : (
              <div>
                {selfPaidList.map((detail, index) => (
                  <SelfPaidList
                    key={detail.polno}
                    datetimeStr={detail.ticket}
                    price={detail.amt}
                    polno={detail.polno}
                    photoaddr={detail.photoaddr}
                    isLastItem={index === paidList.length - 1}
                  />
                ))}
              </div>
            )}
          </Tabs.Tab>
        </Tabs>
      </div>
    </>
  );
};

export default FineTicket;
